import React from 'react';
import { useUser } from '@/contexts/UserContext';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { fetchEntityList } from '@/lib/entity-api';
import { InvoicePaymentForm } from './InvoicePaymentForm';
import { InvoiceService } from '../services/invoiceService';
import { createInvoicePayment } from '../services/invoicePaymentsService';
import { dollars, paymentFigures } from '../lib/invoice-payment-math';
import type { Invoice } from '../types';

interface InvoicePaymentCreateDialogProps {
  isOpen: boolean;
  /** The invoice the payment is for (freshly read: its total and status are what the first amount is worked out from). */
  invoice: Pick<Invoice, 'id' | 'project_id' | 'total_amount'> | null;
  onClose: () => void;
  /** Called with the saved payment; the caller refreshes what the payment changed (the invoice's paid amount, its status). */
  onCreated: (payment: any) => void;
}

/**
 * "Create Payment" from an invoice: THE invoice payment form (the one the Payments table uses), opened on this invoice - its project, and what is left to pay
 * as the first amount, all editable. Saving goes through the same create request; nothing of the form is rebuilt here. The invoice's status is not set by this:
 * the server works it out from the payments that exist.
 */
export function InvoicePaymentCreateDialog({ isOpen, invoice, onClose, onCreated }: InvoicePaymentCreateDialogProps) {
  const { companyId, user } = useUser();
  const { projects } = useCompanyLookups(isOpen && !!companyId, { includeInactiveProjects: true });
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [leftToPay, setLeftToPay] = React.useState<number | null>(null);

  // The form opens once its options and the amount left are known (so the first amount is right from the first render)
  React.useEffect(() => {
    if (!isOpen || !invoice) { setLeftToPay(null); return undefined; }
    let cancelled = false;
    Promise.all([
      InvoiceService.getInvoices(),
      fetchEntityList<any>('invoice_payment', { filters: { invoice_id: invoice.id } }),
    ])
      .then(([list, payments]) => {
        if (cancelled) return;
        setInvoices(list);
        setLeftToPay(dollars(paymentFigures({ totalAmount: invoice.total_amount, payments, amount: 0 }).leftToPayCents));
      })
      .catch((error) => {
        console.error('Error preparing the payment:', error);
        if (!cancelled) { setInvoices([]); setLeftToPay(dollars(paymentFigures({ totalAmount: invoice.total_amount, payments: [], amount: 0 }).leftToPayCents)); }
      });
    return () => { cancelled = true; };
  }, [isOpen, invoice]);

  if (!isOpen || !invoice || leftToPay === null) return null;

  return (
    <InvoicePaymentForm
      payment={null}
      projects={projects as any}
      invoices={invoices}
      defaults={{ invoice_id: invoice.id, project_id: invoice.project_id, amount: leftToPay }}
      onClose={onClose}
      isOpen
      onSave={async ({ paymentData, newAttachments }) => {
        if (!companyId || !user) return undefined;
        const saved = await createInvoicePayment(paymentData, newAttachments, { companyId, userId: user.id });
        onCreated(saved);
        return saved;
      }}
    />
  );
}
